import { useState, useEffect } from 'react';
import { Select, Input, SimpleGrid } from '@chakra-ui/react';
import { ValidatedField } from './index';
import type { ItemPath } from 'api/quote-marketplace';

interface IntegerSelectWithOverflowProps {
  itemPath: ItemPath;
  fieldPath: string;
  label: string;
  exactLabel: string;
  value: number | null;
  onChange: (value: number | null) => void;
  overflowThreshold?: number;
  minExactValue?: number;
  isDisabled?: boolean;
  isLocked?: boolean;
  entity?: Record<string, unknown>;
}

const OVERFLOW_SELECT_VALUE = 'more_than_3';

const IntegerSelectWithOverflow = ({
  itemPath,
  fieldPath,
  label,
  exactLabel,
  value,
  onChange,
  overflowThreshold = 4,
  minExactValue = 4,
  isDisabled = false,
  isLocked = false,
  entity,
}: IntegerSelectWithOverflowProps) => {
  const [showOverflowInput, setShowOverflowInput] = useState(
    value !== null && value >= overflowThreshold,
  );

  useEffect(() => {
    if (value !== null && value >= overflowThreshold) {
      setShowOverflowInput(true);
    } else if (value !== null && value >= 1 && value <= 3) {
      setShowOverflowInput(false);
    }
  }, [value, overflowThreshold]);

  const selectValue = showOverflowInput
    ? OVERFLOW_SELECT_VALUE
    : value === null
      ? ''
      : String(value);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = e.target.value;
    if (newValue === '') {
      setShowOverflowInput(false);
      onChange(null);
    } else if (newValue === OVERFLOW_SELECT_VALUE) {
      setShowOverflowInput(true);
      onChange(null);
    } else {
      setShowOverflowInput(false);
      onChange(parseInt(newValue, 10));
    }
  };

  const handleExactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
    onChange(isNaN(val as number) ? null : val);
  };

  return (
    <SimpleGrid columns={showOverflowInput ? 2 : 1} spacing={4}>
      <ValidatedField
        itemPath={itemPath}
        fieldPath={fieldPath}
        label={label}
        entity={entity}
        isDisabled={isDisabled || isLocked}
        formControlProps={{ whiteSpace: 'nowrap' }}
      >
        <Select
          variant="main"
          size="sm"
          value={selectValue}
          onChange={handleSelectChange}
          isDisabled={isDisabled || isLocked}
        >
          <option value="">Select...</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value={OVERFLOW_SELECT_VALUE}>More than 3</option>
        </Select>
      </ValidatedField>

      {showOverflowInput && (
        <ValidatedField
          itemPath={itemPath}
          fieldPath={fieldPath}
          label={exactLabel}
          entity={entity}
          isDisabled={isDisabled || isLocked}
          formControlProps={{ whiteSpace: 'nowrap' }}
        >
          <Input
            variant="main"
            size="sm"
            type="number"
            min={minExactValue}
            value={value ?? ''}
            onChange={handleExactChange}
            isDisabled={isDisabled || isLocked}
          />
        </ValidatedField>
      )}
    </SimpleGrid>
  );
};

export default IntegerSelectWithOverflow;
